import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { api, type Membership } from '../lib/api';

const RANK_LABEL: Record<string, string> = {
  standard: 'STANDARD',
  high_class: 'PREMIUM',
  platinum: 'PLATINUM',
};

const STATUS_LABEL: Record<string, string> = {
  active: '有効',
  pending: '登録待ち',
  past_due: '支払い遅延',
  canceled: '解約済み',
  invited: '招待',
};

export function MemberCard({
  member,
  onPortal,
}: {
  member: Membership;
  onPortal: () => Promise<void>;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [remaining, setRemaining] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  async function refreshQr() {
    try {
      setError(null);
      const r = await api.qr();
      if (canvasRef.current) {
        await QRCode.toCanvas(canvasRef.current, r.token, {
          width: 240,
          margin: 1,
          errorCorrectionLevel: 'M',
        });
      }
      setRemaining(r.expiresInSec);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    void refreshQr();
  }, []);

  useEffect(() => {
    if (remaining <= 0) return;
    const t = setInterval(() => {
      setRemaining((s) => {
        if (s <= 1) {
          void refreshQr();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [remaining]);

  return (
    <>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className={`rank-${member.rank}`} style={{ fontSize: 18, letterSpacing: '0.15em' }}>
            {RANK_LABEL[member.rank] ?? member.rank}
          </div>
          <span className={`status-pill status-${member.status}`}>
            {STATUS_LABEL[member.status] ?? member.status}
          </span>
        </div>
        <div style={{ marginTop: 12, fontSize: 20 }}>{member.displayName ?? '会員'}</div>
        <div className="muted" style={{ marginTop: 4 }}>
          ID: {member.id.slice(0, 8)}
        </div>
      </div>

      <div className="card">
        <div className="muted" style={{ marginBottom: 8 }}>
          入店認証QR（{remaining}秒で更新）
        </div>
        <div className="qr-wrap">
          <canvas ref={canvasRef} />
        </div>
        {error && <div className="error">{error}</div>}
        <button onClick={() => void refreshQr()}>QRを更新</button>
      </div>

      <div className="card">
        <div className="muted">サブスクリプション</div>
        <div style={{ marginTop: 8 }}>
          ステータス: {member.subscription?.status ?? '—'}
        </div>
        {member.subscription?.currentPeriodEnd && (
          <div className="muted" style={{ marginTop: 4 }}>
            次回更新: {new Date(member.subscription.currentPeriodEnd).toLocaleDateString('ja-JP')}
          </div>
        )}
        <button onClick={() => void onPortal()}>カード変更・解約</button>
      </div>
    </>
  );
}
